import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { motion } from "framer-motion";

const tiers = [
  {
    name: "On-Demand",
    price: "120",
    suffix: "lei / oră",
    desc: "Pentru intervenții punctuale acasă sau la birou.",
    features: [
      "Deplasare on-site la cerere",
      "Diagnoză gratuită",
      "Garanție 30 zile la intervenție",
      "Plata doar la finalizare",
    ],
    cta: "Solicită intervenție",
    featured: false,
  },
  {
    name: "Business Care",
    price: "690",
    suffix: "lei / lună",
    desc: "Cel mai ales abonament. IT-ul tău, monitorizat și documentat.",
    features: [
      "Până la 8 ore intervenție inclusă",
      "Răspuns garantat sub 4 ore",
      "Monitorizare 24/7 a serverelor",
      "Backup automat verificat lunar",
      "Raportare lunară transparentă",
    ],
    cta: "Vreau abonament",
    featured: true,
  },
  {
    name: "Enterprise",
    price: "personalizat",
    suffix: "",
    desc: "Pentru firme cu peste 25 stații sau cerințe complexe.",
    features: [
      "SLA dedicat și manager de cont",
      "Tehnician dedicat la fața locului",
      "Audit IT și plan de modernizare",
      "Politici de securitate ISO-ready",
    ],
    cta: "Discutăm",
    featured: false,
  },
];

export default function Pricing() {
  return (
    <section id="preturi" data-testid="pricing-section" className="py-24 lg:py-36 border-t border-line bg-sandwarm">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline text-clay">PACHETE & PREȚURI</span>
            <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em]">
              Transparent. Fără asteriscuri.
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-inksoft max-w-sm"
          >
            Prețuri orientative. Pentru oferte personalizate, scrie-ne în
            formularul de contact.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <motion.article
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`pricing-${t.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative rounded-2xl p-8 lg:p-10 border ${
                t.featured ? "tracing-card bg-ink text-sand border-ink" : "bg-white border-line"
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-clay text-white text-xs font-semibold tracking-wide uppercase">
                  Recomandat
                </span>
              )}
              <h3 className="font-display text-2xl font-bold tracking-tight">{t.name}</h3>
              <p className={`mt-2 text-sm ${t.featured ? "text-sand/70" : "text-inksoft"}`}>{t.desc}</p>
              <div className="mt-8 flex items-baseline gap-2">
                <span className="font-display text-5xl font-black tracking-tighter">{t.price}</span>
                {t.suffix && (
                  <span className={`text-sm ${t.featured ? "text-sand/70" : "text-inksoft"}`}>
                    {t.suffix}
                  </span>
                )}
              </div>
              <ul className="mt-8 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check size={16} className="mt-0.5 shrink-0 text-clay" />
                    <span className={t.featured ? "text-sand/90" : "text-ink"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`pricing-cta-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`mt-10 inline-flex w-full justify-center px-6 py-4 rounded-full font-semibold text-sm transition-colors ${
                  t.featured
                    ? "bg-clay text-white hover:bg-clayhover"
                    : "bg-ink text-sand hover:bg-clay hover:text-white"
                }`}
              >
                {t.cta}
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
